import {
  CalendarClock,
  Globe,
  Megaphone,
  ShieldCheck,
  Sparkles,
  Wand2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { API_BASE_URL } from "../../shared/constants";
import { VirtusAuthBackground } from "./VirtusAuthBackground";

interface Feature {
  icon: React.ElementType;
  title: string;
  description: string;
  iconClass: string;
}

type ApiStatus = "checking" | "ok" | "down";

/** Consulta o health check real da API (sem indicador fixo/inventado). */
const useApiStatus = (): ApiStatus => {
  const [status, setStatus] = useState<ApiStatus>("checking");

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 6000);

    fetch(`${API_BASE_URL}/health`, { signal: controller.signal })
      .then((res) => setStatus(res.ok ? "ok" : "down"))
      .catch(() => {
        if (!controller.signal.aborted || status === "checking")
          setStatus("down");
      })
      .finally(() => window.clearTimeout(timeout));

    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return status;
};

/**
 * Painel de marca (lado direito, só em telas grandes).
 * O texto descreve apenas o que o produto faz hoje — sem selos de parceria nem status
 * de sistema que a tela não consegue comprovar.
 */
export const AuthBrandingPanel: React.FC = () => {
  const { t } = useTranslation();
  const apiStatus = useApiStatus();

  const features: Feature[] = [
    {
      icon: CalendarClock,
      title: t("Publicações com IA e agendamento"),
      description: t(
        "Crie posts para Facebook, Instagram e LinkedIn e programe com o Cronograma IA.",
      ),
      iconClass: "bg-violet-600/20 text-violet-300 group-hover:bg-violet-600",
    },
    {
      icon: Megaphone,
      title: t("Anúncios e leads"),
      description: t(
        "Campanhas guiadas em Meta, Google e LinkedIn, com formulário de leads, funil Kanban e atribuição por campanha.",
      ),
      iconClass: "bg-indigo-600/20 text-indigo-300 group-hover:bg-indigo-600",
    },
    {
      icon: Wand2,
      title: t("Conteúdo e estratégia com IA"),
      description: t(
        "Studio de Vídeo com clonagem de voz, e-books, mapeamento de público (ICP) e recomendações baseadas em resultados reais.",
      ),
      iconClass: "bg-purple-600/20 text-purple-300 group-hover:bg-purple-600",
    },
  ];

  return (
    <div className="relative flex h-full w-full flex-col justify-between overflow-hidden bg-slate-950 px-8 py-6 text-white xl:px-14 xl:py-10 2xl:px-16 2xl:py-12">
      <VirtusAuthBackground />

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[480px] flex-col justify-between xl:max-w-[540px]">
        {/* Topo — alinhado ao cabeçalho da coluna do formulário */}
        <div className="flex h-9 shrink-0 items-center justify-between gap-3 text-xs xl:h-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 font-medium text-violet-300 backdrop-blur-xs">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-violet-400" />
            <span>{t("Plataforma de Marketing Digital")}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-400">
            <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" />
            <span>{t("Conta única Virtus")}</span>
          </div>
        </div>

        {/* Proposta de valor + destaques */}
        <div className="my-auto flex w-full flex-col justify-center space-y-6 py-3 xl:space-y-7">
          <div className="space-y-3">
            <h2 className="text-3xl leading-tight font-bold tracking-tight text-white 2xl:text-4xl">
              {t("Redes sociais e anúncios em")}{" "}
              <span className="bg-linear-to-r from-violet-400 via-purple-300 to-indigo-300 bg-clip-text text-transparent">
                {t("um só lugar")}
              </span>
            </h2>
            <p className="text-sm leading-relaxed text-slate-300">
              {t(
                "Crie e programe conteúdo, gerencie campanhas e acompanhe leads e resultados sem trocar de ferramenta. Use o mesmo login em todas as soluções Virtus.",
              )}
            </p>
          </div>

          <div className="grid gap-2.5">
            {features.map(({ icon: Icon, title, description, iconClass }) => (
              <div
                key={title}
                className="group flex items-start gap-3.5 rounded-xl border border-white/10 bg-white/4 p-3 backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-violet-500/40 hover:bg-white/[0.07] 2xl:p-4"
              >
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors group-hover:text-white ${iconClass}`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-white">{title}</h3>
                  <p className="mt-0.5 text-xs leading-normal text-slate-400">
                    {description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Base — alinhada ao rodapé da coluna do formulário */}
        <div className="mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-white/10 pt-4 text-xs text-slate-500 xl:pt-6">
          <div className="flex items-center gap-2" role="status">
            <span className="relative flex h-2 w-2">
              {apiStatus === "ok" && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-40" />
              )}
              <span
                className={`relative inline-flex h-2 w-2 rounded-full ${
                  apiStatus === "ok"
                    ? "bg-emerald-500"
                    : apiStatus === "down"
                      ? "bg-amber-500"
                      : "bg-slate-500"
                }`}
              />
            </span>
            <span className="font-medium text-slate-400">
              {apiStatus === "ok"
                ? t("Sistemas Operacionais")
                : apiStatus === "down"
                  ? t("Instabilidade detectada")
                  : t("Verificando sistemas...")}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Globe className="h-3 w-3" />
            <span>{t("Português e inglês")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
